import {useState} from 'react';
import {formatDistanceToNow} from 'date-fns';
import {History, Search} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {useQcHistory} from '@/api/hooks';
import {type QcHistoryEntry, type QcHistoryOutcome} from '@/api/qcReview';
import {InFilterBar} from '@/components/layout/FilterBarSlot';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {TruncatedText} from '@/components/shared/TruncatedText';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {useAuth} from '@/context/useAuth';
import {useDebounce} from '@/lib/useDebounce';

const PAGE_SIZE = 25;
const COLUMN_COUNT = 8;

const OUTCOMES: Record<
  QcHistoryOutcome,
  {label: string; variant: 'default' | 'secondary' | 'destructive'}
> = {
  approved: {label: 'Approved', variant: 'default'},
  approved_with_edits: {label: 'Approved with edits', variant: 'secondary'},
  rejected: {label: 'Rejected', variant: 'destructive'}
};

const isOutcome = (value: string): value is QcHistoryOutcome => value in OUTCOMES;

/**
 History tab: every QC decision, newest first. Opens on the signed-in reviewer's own decisions;
 Everyone widens it to the whole team's. Its controls portal into the page's filter bar.
 */
export function QcHistory({verticals}: {readonly verticals: string[]}) {
  const {user} = useAuth();
  const [mineOnly, setMineOnly] = useState(true);
  const [outcome, setOutcome] = useState<QcHistoryOutcome | ''>('');
  const [vertical, setVertical] = useState('');
  const [analyst, setAnalyst] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const debouncedAnalyst = useDebounce(analyst.trim(), 300);

  // Any filter change goes back to the first page.
  function filter<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      setPage(1);
    };
  }

  const {data, isLoading} = useQcHistory({
    // AuthUser.username is the email - the API mints the claim from it (see ICurrentUser.Email).
    ...(mineOnly && user ? {reviewer: user.username} : {}),
    ...(outcome ? {outcome} : {}),
    ...(vertical ? {policy_violation: vertical} : {}),
    ...(debouncedAnalyst ? {analyst: debouncedAnalyst} : {}),
    // The date inputs are local calendar days; the range is inclusive of the whole "to" day.
    ...(from ? {date_from: new Date(`${from}T00:00:00`).toISOString()} : {}),
    ...(to ? {date_to: new Date(`${to}T23:59:59.999`).toISOString()} : {}),
    page,
    page_size: PAGE_SIZE
  });
  const items = data?.results ?? [];
  const total = data?.total_count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-4">
      <InFilterBar>
        <ToggleGroup
          variant="outline"
          spacing={0}
          size="sm"
          aria-label="Reviewed by"
          value={[mineOnly ? 'me' : 'everyone']}
          onValueChange={(next) => {
            // An empty `next` is a re-click on the pressed option, which keeps it.
            if (next[0]) {
              filter(setMineOnly)(next[0] === 'me');
            }
          }}>
          <ToggleGroupItem value="me">My decisions</ToggleGroupItem>
          <ToggleGroupItem value="everyone">Everyone</ToggleGroupItem>
        </ToggleGroup>
        <NativeSelect
          size="sm"
          aria-label="Outcome"
          value={outcome}
          onChange={(event) => {
            const {value} = event.target;
            filter(setOutcome)(isOutcome(value) ? value : '');
          }}>
          <NativeSelectOption value="">All outcomes</NativeSelectOption>
          {Object.entries(OUTCOMES).map(([value, {label}]) => (
            <NativeSelectOption key={value} value={value}>
              {label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <NativeSelect
          size="sm"
          aria-label="Vertical"
          value={vertical}
          onChange={(event) => {
            filter(setVertical)(event.target.value);
          }}>
          <NativeSelectOption value="">All verticals</NativeSelectOption>
          {verticals.map((v) => (
            <NativeSelectOption key={v} value={v}>
              {v}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <InputGroup className="w-56 max-w-full">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            value={analyst}
            placeholder="Analyst email"
            aria-label="Analyst email"
            onChange={(event) => {
              filter(setAnalyst)(event.target.value);
            }}
          />
        </InputGroup>
        <Field orientation="horizontal" className="w-auto gap-1.5">
          <FieldLabel htmlFor="qc-history-from">From</FieldLabel>
          <Input
            id="qc-history-from"
            type="date"
            value={from}
            className="w-40"
            onChange={(event) => {
              filter(setFrom)(event.target.value);
            }}
          />
        </Field>
        <Field orientation="horizontal" className="w-auto gap-1.5">
          <FieldLabel htmlFor="qc-history-to">To</FieldLabel>
          <Input
            id="qc-history-to"
            type="date"
            value={to}
            className="w-40"
            onChange={(event) => {
              filter(setTo)(event.target.value);
            }}
          />
        </Field>
      </InFilterBar>

      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Content</TableHead>
              <TableHead>Analyst</TableHead>
              <TableHead>Policy Violation</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Review notes</TableHead>
              <TableHead>Reviewed by</TableHead>
              <TableHead>Reviewed</TableHead>
              <TableHead>Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <History />
                      </EmptyMedia>
                      <EmptyTitle>No QC decisions</EmptyTitle>
                      <EmptyDescription>
                        Nothing has been approved or rejected under these filters.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {items.map((item) => (
              <QcHistoryRow key={`${item.outcome}-${item.id}`} item={item} />
            ))}
          </TableBody>
        </Table>
      </TablePanel>

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-end gap-2 text-xs">
          <span className="text-muted-foreground">
            Page {page} of {pageCount} · {total} total
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => {
              setPage(page - 1);
            }}>
            Previous
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={page >= pageCount}
            onClick={() => {
              setPage(page + 1);
            }}>
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function QcHistoryRow({item}: {readonly item: QcHistoryEntry}) {
  const outcome = OUTCOMES[item.outcome];
  const title = item.title ?? item.item_external_id ?? item.submission_id;
  const edit = [item.edited_policy_violation, item.edited_risk].filter(Boolean).join(' / ');

  return (
    <TableRow>
      <TableCell className="max-w-[260px] text-xs">
        <div className="flex items-center gap-1.5">
          <TruncatedText className="font-medium">{title}</TruncatedText>
          {item.url ? <CopyUrlButton url={item.url} className="shrink-0" /> : null}
        </div>
      </TableCell>
      <TableCell className="text-xs">{item.analyst_email}</TableCell>
      <TableCell className="max-w-[260px] min-w-[180px] text-xs break-words whitespace-normal">
        {item.policy_violation} / {item.original_risk}
      </TableCell>
      <TableCell className="text-xs">
        <Badge variant={outcome.variant}>{outcome.label}</Badge>
        {edit ? <p className="text-muted-foreground mt-1">Changed to {edit}</p> : null}
      </TableCell>
      <TableCell className="max-w-[360px] min-w-[200px] text-xs break-words whitespace-pre-wrap">
        {item.review_notes ?? '—'}
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">{item.reviewed_by}</TableCell>
      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
        {formatDistanceToNow(new Date(item.reviewed_at), {addSuffix: true})}
      </TableCell>
      <TableCell className="text-xs">{item.submission_type ?? '—'}</TableCell>
    </TableRow>
  );
}
